/**
* @name: index
* @author: weiyong
* @date: 2023-03-31 18:15
* @description：index
* @update: 2023-03-31 18:15
*/
<template>
  <div :class="prefixCls">
    <div class="left-div-area" style="background-color: #eee;">
      <base-set :userSetting="userSetting" :useLayout="useLayout" @setLayout="setLayout"/>
      <page-layout-set @setLayout="setLayout" :useLayout="useLayout"/>
      <page-header-bg-set ref="headerBgSetCtl" @closeOtherColor="closeOtherColor"/>
      <page-menu-bg-set :useLayout="useLayout" ref="menuSetCtl" @closeOtherColor="closeOtherColor"/>
      <page-background-set ref="pageBgCtl" @closeOtherColor="closeOtherColor"/>
      <page-primary-color ref="headerBgCtl" @closeOtherColor="closeOtherColor"/>
      <base-raduis-set/>
      <base-font-size-set/>
      <left-menu-space/>
      <page-card-space/>
    </div>
    <div class="right-div-area" style="position: relative">
      <layoutDefault :settingLayout="false" :analysisSet="true" :useLayout="useLayout"
                     v-show="showRouterFlag" id="main_router_view"/>
    </div>

  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

export default defineComponent({
  components: {},
  name: "index",
});
</script>

<script lang="ts" setup>
import BaseRaduisSet from "@/views/dashboard/analysisSet/components/base-raduis-set.vue";
import BaseFontSizeSet from "@/views/dashboard/analysisSet/components/base-font-size-set.vue";
import LeftMenuSpace from "@/views/dashboard/analysisSet/components/left-menu-space.vue";
import PagePrimaryColor from "@/views/dashboard/analysisSet/components/page-primary-color.vue";
import {useDesign} from "@/hooks/web/useDesign";
import BaseSet from "@/views/dashboard/analysisSet/components/base-set.vue";
import LayoutDefault from '@/layouts/default/index.vue';
import {useRouter} from "vue-router";
import PageHeaderBgSet from "@/views/dashboard/analysisSet/components/page-header-bg-set.vue";
import PageCardSpace from "@/views/dashboard/analysisSet/components/page-card-space.vue";
import PageBackgroundSet from "@/views/dashboard/analysisSet/components/page-background-set.vue";
import PageLayoutSet from "@/views/dashboard/analysisSet/components/page-layout-set.vue";
import {removeMenuAction, saveLayoutConfig} from "@/api/sys/portalSpace";
import GoingUtils from "@/utils/goingUtils";
import {useAppStore} from "@/store/modules/app";
import {useI18n} from "vue-i18n";
import PageMenuBgSet from "@/views/dashboard/analysisSet/components/page-menu-bg-set.vue";
import {StorageUtils} from 'xmgoing-utils';
import {nextTick, toRaw} from "vue";
import { useAppFrame } from '@/hooks/useAppFrame';
import {baseHandler} from "@/layouts/default/setting/handler";
import {HandlerEnum} from "@/layouts/default/setting/enum";
import defaultProjectSetting from '@/settings/projectSetting';
import {
  HEADER_PRESET_BG_COLOR_LIST,
  SIDE_BAR_BG_COLOR_LIST,
} from '@/settings/designSetting';
import { Persistent } from '@/utils/cache/persistent';
import { PROJ_CFG_KEY } from '@/enums/cacheEnum';
import { getFrameColorPreset } from '@/settings/frameColorPreset';

interface LayoutPreset {
  headerBg: string;
  menuBg: string;
  showSearch: boolean;
  showTabsQuick: boolean;
  showTabsFold: boolean;
}

/** 经典/顶部/二级菜单等框架的默认配置（与 projectSetting 一致） */
const DEFAULT_LAYOUT_PRESET: LayoutPreset = {
  headerBg: defaultProjectSetting.headerSetting?.bgColor || HEADER_PRESET_BG_COLOR_LIST[0],
  menuBg: defaultProjectSetting.menuSetting?.bgColor || SIDE_BAR_BG_COLOR_LIST[0],
  showSearch: defaultProjectSetting.headerSetting?.showSearch ?? true,
  showTabsQuick: defaultProjectSetting.multiTabsSetting?.showQuick ?? true,
  showTabsFold: defaultProjectSetting.multiTabsSetting?.showFold ?? true,
};

/** 各门户框架主题的专属默认配置 */
const LAYOUT_PRESETS: Partial<Record<string, LayoutPreset>> = {
  technology: {
    headerBg: '#ffffff',
    menuBg: '#ffffff',
    showSearch: false,
    showTabsQuick: false,
    showTabsFold: false,
  },
  classicBlue: {
    headerBg: '#1F58A7',
    menuBg: '#1F58A7',
    showSearch: false,
    showTabsQuick: false,
    showTabsFold: false,
  },
};

const {t} = useI18n();
const appStore = useAppStore();
appStore.showRouterFlag = true;
const showRouterFlag = ref(false);
const {prefixCls} = useDesign('index');
const useLayout = ref(StorageUtils.getItem("layout") || 'defaultFrame');

const router = useRouter();
showRouterFlag.value = true;
router.push({path: "/"});


const { setFrameType } = useAppFrame();

const headerBgSetCtl: any = ref(null);
const headerBgCtl: any = ref(null);
const menuSetCtl: any = ref(null);
const pageBgCtl: any = ref(null);

/** 同步写入主题到 store/CSS，保证框架切换后预览立刻用到新颜色 */
const applyLayoutPresetToStore = (layout: string) => {
  const preset = LAYOUT_PRESETS[layout] ?? DEFAULT_LAYOUT_PRESET;
  baseHandler(HandlerEnum.HEADER_THEME, preset.headerBg);
  baseHandler(HandlerEnum.MENU_THEME, preset.menuBg);
  baseHandler(HandlerEnum.HEADER_SEARCH, preset.showSearch);
  baseHandler(HandlerEnum.TABS_SHOW_QUICK, preset.showTabsQuick);
  baseHandler(HandlerEnum.TABS_SHOW_FOLD, preset.showTabsFold);
  return preset;
};

/** 同步左侧色板选中态（需在子组件 ref 就绪后） */
const syncLayoutPresetPickers = (preset: LayoutPreset) => {
  headerBgSetCtl.value?.selItem({ id: preset.headerBg, color: preset.headerBg });
  menuSetCtl.value?.selItem({ id: preset.menuBg, color: preset.menuBg });
};

/** 切换布局时应用该主题的默认顶栏/菜单/头部与标签配置 */
const applyLayoutPreset = (layout: string) => {
  const preset = applyLayoutPresetToStore(layout);
  nextTick(() => {
    syncLayoutPresetPickers(preset);
  });
};

//设置布局方式
const setLayout = function (layout) {
  // 先写入主题，再切框架，避免新框架挂载时仍读到上一套颜色（表现为晚一拍）
  const preset = applyLayoutPresetToStore(layout);
  useLayout.value = layout;
  setFrameType(layout);
  StorageUtils.addItem("layout", layout);
  StorageUtils.addSessionItem('xm_preferred_layout', layout);
  // 同步到 projectConfig，确保关闭抽屉保存时带上最新 layoutId
  appStore.setProjectConfig({ layoutId: layout } as any);
  nextTick(() => {
    syncLayoutPresetPickers(preset);
    // 布局 remount 后再刷一次，防止挂载过程覆盖主题
    applyLayoutPresetToStore(layout);
  });
}

interface IProps {
  userSetting: boolean
}

const props = withDefaults(defineProps<IProps>(), {
  userSetting: false,
})


const closeOtherColor = function () {
  headerBgSetCtl.value.closePanel();
  headerBgCtl.value.closePanel();
  menuSetCtl.value.closePanel();
  pageBgCtl.value.closePanel();
}
/**
 * 保存配置信息（与个性化设置右上角「保存」同一入口）
 * @param layoutId
 */
const saveMenuSetting = async function (layoutId) {
  // 优先用当前选中的框架（Storage 在切换时已同步），避免仍保存旧 layoutId
  const latestLayout =
    layoutId ||
    unref(useLayout) ||
    StorageUtils.getItem('layout') ||
    appStore.layout;
  if (!latestLayout) {
    throw new Error('未获取到当前框架 layoutId');
  }
  // 避免 Deepclone 对响应式代理处理不完整，用 JSON 做纯对象拷贝
  const projectConfig: any = JSON.parse(JSON.stringify(toRaw(appStore.projectConfig) || {}));
  projectConfig.layoutId = latestLayout;
  // 按框架写入对应顶栏/菜单色，避免刷新后颜色仍是上一套框架
  const frameColorPreset = getFrameColorPreset(latestLayout);
  if (frameColorPreset) {
    projectConfig.headerSetting = {
      ...(projectConfig.headerSetting || {}),
      bgColor: frameColorPreset.headerBg,
    };
    projectConfig.menuSetting = {
      ...(projectConfig.menuSetting || {}),
      bgColor: frameColorPreset.menuBg,
    };
  }
  const configJson = JSON.stringify(projectConfig);

  // 先写本地：刷新时即使接口稍慢/失败，也能保持选中的框架
  StorageUtils.addItem('layout', latestLayout);
  StorageUtils.addSessionItem('xm_preferred_layout', latestLayout);
  StorageUtils.addSessionItem('defaultLayoutConfig', configJson);
  Persistent.setLocal(PROJ_CFG_KEY, projectConfig, true);
  appStore.setLayout(latestLayout);
  appStore.setProjectConfig({ layoutId: latestLayout } as any);

  await saveLayoutConfig('defaultLayout', configJson);
  GoingUtils.showMessage(t("保存成功！"));
}

/**
 * 删除配置信息
 * @param layoutId
 */
const removeMenuSetting = async function (layoutId) {
  await removeMenuAction();
  GoingUtils.showMessage(t("操作成功！"));
}
/**
 * 处理消息信息
 * @param message
 */
const handleMessage = (message: any) => {
  const {action} = message.data;
  if (action === "saveLayoutDes") {
    saveMenuSetting(unref(useLayout));
  }
  if (action === "removeMenuSetting") {
    removeMenuSetting(unref(useLayout));
  }
};

onMounted(() => {
  window.appObj.analysisSet = true;
  window.addEventListener("message", handleMessage);
  applyLayoutPreset(unref(useLayout));
});

onBeforeUnmount(() => {
  window.removeEventListener("message", handleMessage);
  // 离开设计页必须复位，否则 SimpleMenu（生产行业/经典模式等）菜单点击会被拦截无法跳转
  window.appObj.analysisSet = false;
});
defineExpose({saveMenuSetting, removeMenuSetting})
</script>

<style type="text/css" lang="less">
@prefix-cls: ~'@{namespace}-index';

.@{prefix-cls} {
  width: 100%;
  overflow: hidden;
  height: 100%;
  display: flex;

  .left-div-area {
    width: 330px;
    padding: 8px;
    height: 100%;
    overflow: auto;
  }

  .right-div-area {
    flex: 1;
    width: calc(100% - 330px);
  }

  .ant-layout-sider.vben-layout-sideBar--fixed {
    position: absolute;
  }

  .vben-layout-multiple-header--fixed {
    position: absolute;
  }

  .vben-layout-mix-sider {
    position: absolute;
  }
}
</style>
